import {FC, RefObject, useEffect, useState} from 'react';
import {TrackReferenceOrPlaceholder, VideoTrack} from '@livekit/components-react';
import {ILiveStreamVideo, IUseLiveKitResult, TRect, useVideoPosition} from 'core';
import {useScreenSize} from '@kcast-fe/ui-kit';
import {Box, Portal} from '@chakra-ui/react';
import {Rnd} from 'react-rnd';
import {Svg} from 'ui-kit';

import {LiveStreamWindow} from './components';

interface IProps {
  lk: IUseLiveKitResult;
  isAvatarOnly: boolean;
  cRef: RefObject<HTMLDivElement | null>;
  primaryVideo?: TrackReferenceOrPlaceholder | null;
  secondaryVideo?: TrackReferenceOrPlaceholder | null;
  liveStreamVideos: ILiveStreamVideo[];
}

const PRIMARY_KEY = 'malenia:video:1';
const SECONDARY_KEY = 'malenia:video:2';
// Live stream windows: malenia:video:3 for Camera 1, :4 for Camera 2, …
const LIVE_STREAM_KEY_BASE = 3;
const LIVE_STREAM_GAP = 12;
// The avatar is always on top; the primary video and the live streams swap on click
const Z_INDEX = {back: 1, front: 2, avatar: 3};

type TFrontVideo = 'primary' | 'liveStream';

export const VideoRender: FC<IProps> = ({
  lk,
  cRef,
  primaryVideo,
  secondaryVideo,
  liveStreamVideos,
  isAvatarOnly
}) => {
  const {isLessThan768} = useScreenSize();

  const MIN_WIDTH = 120;
  const MIN_HEIGHT = 120;

  const PRIMARY_DEFAULT: TRect = isLessThan768
    ? {x: 12, y: 12, width: 300, height: 300}
    : {x: 12, y: 12, width: 480, height: 270};

  const PRIMARY_AVATAR_ONLY_DEFAULT: TRect = isLessThan768
    ? {x: 12, y: 12, width: 300, height: 300}
    : {x: 12, y: 12, width: 270, height: 270};

  const SECONDARY_DEFAULT: TRect = isLessThan768
    ? {x: 24, y: 24, width: 150, height: 150}
    : {x: 24, y: 24, width: 180, height: 180};

  // Equirectangular 360 stream is 2:1. Further cameras are stacked below the first one.
  const liveStreamDefault = (index: number): TRect => {
    const [width, height, y] = isLessThan768 ? [300, 150, 324] : [540, 270, 12];
    return {x: isLessThan768 ? 12 : 504, y: y + index * (height + LIVE_STREAM_GAP), width, height};
  };

  const primaryFallback = isAvatarOnly ? PRIMARY_AVATAR_ONLY_DEFAULT : PRIMARY_DEFAULT;
  const primary = useVideoPosition(PRIMARY_KEY, primaryFallback);
  const secondary = useVideoPosition(SECONDARY_KEY, SECONDARY_DEFAULT);

  const [resizeKey, setResizeKey] = useState(0);
  const [front, setFront] = useState<TFrontVideo>('liveStream');

  const focusPrimary = () => setFront('primary');
  const focusLiveStream = () => setFront('liveStream');

  useEffect(() => {
    const onResize = () => {
      primary.reset();
      secondary.reset();
      setResizeKey((prev) => prev + 1);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [primary, secondary]);

  if (!primary.default || !secondary.default) {
    return null;
  }

  return (
    <Portal container={cRef} disabled={false}>
      {/* Overlay lets videos be dragged anywhere without blocking chat scroll/clicks */}
      <Box position={'absolute'} inset={'0'} zIndex={10} pointerEvents={'none'} overflow={'hidden'}>
        {/* PRIMARY VIDEO */}
        {primaryVideo?.publication && (
          <Rnd
            key={`primary-${resizeKey}`}
            default={primary.default}
            minWidth={MIN_WIDTH}
            minHeight={MIN_HEIGHT}
            bounds={'parent'}
            style={{
              pointerEvents: 'auto',
              zIndex: front === 'primary' ? Z_INDEX.front : Z_INDEX.back
            }}
            onDragStart={focusPrimary}
            onResizeStart={focusPrimary}
            onDragStop={primary.onDragStop}
            onResizeStop={primary.onResizeStop}
          >
            <Box
              position={'relative'}
              w={'100%'}
              h={'100%'}
              color={'white'}
              borderRadius={'12px'}
              overflow={'hidden'}
            >
              <VideoTrack
                trackRef={primaryVideo}
                style={{width: '100%', height: '100%', objectFit: 'cover'}}
              />

              {/* SWITCH CAMERA */}
              {lk.cameraAmount > 1 && (
                <Box position={'absolute'} top={'12px'} right={'12px'} h={'12px'}>
                  <Svg name={'Refresh'} size={'s24'} onClick={lk.switchCamera} />
                </Box>
              )}
            </Box>
          </Rnd>
        )}

        {/* SECONDARY VIDEO */}
        {secondaryVideo?.publication && (
          <Rnd
            key={`secondary-${resizeKey}`}
            default={secondary.default}
            minWidth={MIN_WIDTH}
            minHeight={MIN_HEIGHT}
            bounds={'parent'}
            style={{pointerEvents: 'auto', zIndex: Z_INDEX.avatar}}
            onDragStop={secondary.onDragStop}
            onResizeStop={secondary.onResizeStop}
          >
            <Box w={'100%'} h={'100%'} borderRadius={'8px'} overflow={'hidden'}>
              <VideoTrack
                trackRef={secondaryVideo}
                style={{width: '100%', height: '100%', objectFit: 'cover'}}
              />
            </Box>
          </Rnd>
        )}

        {/* LIVE STREAMS FROM THE USER'S CAMERAS (RICOH THETA X), ONE WINDOW EACH */}
        {liveStreamVideos.map(({video, index}) => (
          <LiveStreamWindow
            key={`${video.participant.identity}-${resizeKey}`}
            video={video}
            storageKey={`malenia:video:${LIVE_STREAM_KEY_BASE + index}`}
            fallback={liveStreamDefault(index)}
            minWidth={MIN_WIDTH}
            minHeight={MIN_HEIGHT}
            zIndex={front === 'liveStream' ? Z_INDEX.front : Z_INDEX.back}
            onFocus={focusLiveStream}
          />
        ))}
      </Box>
    </Portal>
  );
};
